import { LogOut, Settings as SettingsIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { fullName, type AuthenticatedUser } from '@/auth/types';
import { useLogout } from '@/auth/useLogout';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * UserMenu — topbar dropdown with the current user's identity and
 * the logout action. Avatar uses the user's first/last initials over
 * the brand-navy primary surface.
 */
function initials(u: AuthenticatedUser): string {
  const a = (u.firstName ?? '').charAt(0);
  const b = (u.lastName ?? '').charAt(0);
  const composed = `${a}${b}`.toUpperCase();
  return composed.length > 0 ? composed : (u.email[0] ?? '?').toUpperCase();
}

interface UserMenuProps {
  user: AuthenticatedUser;
}

export function UserMenu({ user }: UserMenuProps) {
  const { t } = useTranslation();
  const logout = useLogout();
  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-full p-1 text-sm font-medium outline-none ring-offset-background transition-colors hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label={fullName(user)}
        >
          <span
            aria-hidden
            className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
          >
            {initials(user)}
          </span>
          <span className="hidden max-w-[160px] truncate sm:inline">
            {fullName(user)}
          </span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>{t('userMenu.signedInAs')}</DropdownMenuLabel>
        <div className="px-2 pb-2">
          <p className="truncate text-sm font-medium">{fullName(user)}</p>
          <p className="truncate text-xs text-muted-foreground">{user.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/settings')}>
          <SettingsIcon aria-hidden className="h-4 w-4" />
          {t('userMenu.settings')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => logout.mutate()}
          className="text-destructive focus:bg-destructive/10 focus:text-destructive"
          disabled={logout.isPending}
        >
          <LogOut aria-hidden className="h-4 w-4" />
          {t('common.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
